import { useEffect, useRef, useState } from 'react'

import { ArrowRight } from '@phosphor-icons/react'
import { useMutation, useSuspenseQueries } from '@tanstack/react-query'
import {
  createFileRoute,
  linkOptions,
  useBlocker,
} from '@tanstack/react-router'
import confetti from 'canvas-confetti'

import { Button, ButtonLoading } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Separator } from '@/components/ui/separator'
import {
  TaskLayout,
  TaskLayoutBreadcrumbs,
  TaskLayoutContent,
  TaskLayoutQuestion,
  TaskLayoutQuestionCard,
  TaskLayoutQuestionPagination,
  TaskLayoutStepper,
} from '@/components/view/task/task-layout'
import { TaskListening } from '@/components/view/task/task-listening'
import { TaskMultipleChoice } from '@/components/view/task/task-multiple-choice'
import {
  IStepperStatus,
  Stepper,
} from '@/components/view/task/task-stepper-simple'
import { IQuestionItem, IQuestionStatus } from '@/components/view/task/type'
import { formatTime, useTaskQuiz } from '@/components/view/task/use-task-quiz'

import { useAuth } from '@/services/auth/auth.store'
import {
  trackDetails,
  trackQuiz,
  trackQuizCheckAnswer,
  trackQuizCheckAnswerBatch,
} from '@/services/track/track.gql'

import { formatNaturalTime } from '@/lib/format'
import { invalidateQueries } from '@/lib/utils'

export const Route = createFileRoute('/(app)/track/$trackid/exam/$examid')({
  async loader({ params, preload }) {
    if (preload) return
    useAuth.getState().access('authenticated')

    useTaskQuiz.getState().reset()
    await useTaskQuiz.getState().setup(params.trackid, params.examid)
  },
  component: RouteComponent,
})

function RouteComponent() {
  const shouldBlock = useTaskQuiz(
    (s) => s.status === 'running' || s.status === 'timeout'
  )
  useBlocker({
    shouldBlockFn: () => {
      if (!shouldBlock) return false
      const confirmed = confirm('Apakah anda yakin meninggalkan halaman ini?')
      return !confirmed
    },
    withResolver: true,
  })

  return (
    <TaskLayout>
      <Breadcrumbs />
      <Content />
    </TaskLayout>
  )
}

function useData() {
  const params = Route.useParams()
  const [track, quiz] = useSuspenseQueries({
    queries: [
      trackDetails.qry({ trackId: params.trackid, myTrack: true }),
      trackQuiz.qry({ chapterId: params.examid }),
    ],
  })

  const chapter = quiz.data.getQuizChapterByChapterID.quiz_chapter[0]!

  return {
    track: track.data,
    quiz: quiz.data.getQuizChapterByChapterID,
    chapter,
  }
}

function Breadcrumbs() {
  const { track, quiz } = useData()

  return (
    <TaskLayoutBreadcrumbs
      items={[
        ['Kelas', linkOptions({ from: Route.fullPath, to: '/' })],
        [
          track.getDetailTrack.title,
          linkOptions({ from: Route.fullPath, to: '/track/$trackid' }),
        ],
        [quiz.quiz_chapter[0]!.chapter.title!, Route.fullPath],
      ]}
    />
  )
}

function Content() {
  return (
    <TaskLayoutContent>
      <QuizStepper />
      <ContentQuestion />
    </TaskLayoutContent>
  )
}

function QuizStepper() {
  const questions = useTaskQuiz((s) => s.questions)
  const selected = useTaskQuiz((s) => s.selected)
  const quizStatus = useTaskQuiz((s) => s.status)

  return (
    <TaskLayoutStepper>
      <h2 className="px-2 text-lg font-bold">Soal</h2>
      <Stepper>
        {questions.map((q, index) => {
          const data = selected?.id && q.id === selected.id ? selected : q
          let status: IStepperStatus = 'disable'
          if (quizStatus !== 'running') status = 'disable'
          if (selected?.id && data.id === selected.id) status = 'focus'
          if (data.status === 'correct' || data.status === 'wrong')
            status = 'complete'

          return (
            <Stepper.Item
              key={data.id}
              state={{
                status,
                label: data.title,
                desc: `Soal ${index + 1}`,
                onClick: () => {},
              }}
            />
          )
        })}
      </Stepper>
    </TaskLayoutStepper>
  )
}

function ContentQuestion() {
  const selected = useTaskQuiz((s) => s.selected)
  const status = useTaskQuiz((s) => s.status)

  return (
    <TaskLayoutQuestion>
      <DialogTimeout />
      <TaskLayoutQuestionCard className="relative">
        {status === 'idle' && <OnBoarding />}
        {(status === 'running' || status === 'timeout') && selected && (
          <>
            <div className="absolute top-3 right-3">
              <Timer />
            </div>
            <QuestionContent {...selected} />
          </>
        )}
        {status === 'finished' && <Result />}
      </TaskLayoutQuestionCard>
      <TaskLayoutQuestionPagination>
        <Pagination />
      </TaskLayoutQuestionPagination>
    </TaskLayoutQuestion>
  )
}

function OnBoarding() {
  const title = useTaskQuiz((s) => s.quiz.title)
  const duration = useTaskQuiz((s) => s.duration)
  const guide = useTaskQuiz((s) => s.guide)
  const questionsCount = useTaskQuiz((s) => s.questions.length)

  return (
    <div>
      <div>
        <h2 className="text-center font-semibold">{title}</h2>
      </div>
      <div className="mx-auto mt-8 flex max-w-64 flex-wrap justify-between text-center">
        <div>
          <p className="text-sm">Jumlah Soal</p>
          <p className="text-2xl font-bold">{questionsCount}</p>
        </div>
        <div>
          <p className="text-sm">Durasi Pengerjaan</p>
          <p className="text-2xl font-bold">{formatNaturalTime(duration)}</p>
        </div>
      </div>
      <div className="mt-6 mb-3">
        <h2 className="text-center font-semibold">Panduan</h2>
        <div dangerouslySetInnerHTML={{ __html: guide }} />
      </div>
    </div>
  )
}

function Result() {
  const status = useTaskQuiz((s) => s.status)
  const questions = useTaskQuiz((s) => s.questions)
  const quizId = useTaskQuiz((s) => s.quizId)

  const { isPending, mutateAsync, data } = useMutation({
    mutationKey: ['track-quiz-check-answer-batch', quizId],
    mutationFn: trackQuizCheckAnswerBatch.api,
    onSuccess() {
      invalidateQueries(trackDetails.key)
    },
  })

  useEffect(() => {
    if (status === 'finished') {
      mutateAsync({
        req: {
          answer: questions.map((q) => ({
            answers: q.answerId,
            question_id: q.id,
          })),
          packages: '',
          chapter_id: quizId,
        },
      })
    }
  }, [mutateAsync, status])

  if (isPending || !data) return null

  const { answerListQuizChapter: result } = data

  return (
    <div className="flex grow flex-col justify-between gap-8 p-3">
      <div className="mx-auto w-1/3">
        <img
          src="/img/other/quiz-result.png"
          alt="success"
          width={240}
          height={279.931640625}
          className="w-full"
        />
      </div>
      <div className="flex flex-row flex-wrap items-center justify-evenly gap-4">
        <div className="w-1/4 min-w-fit space-y-1 text-center">
          <p className="font-semibold text-nowrap">Skor kamu</p>
          <p className="text-xl font-black text-[#0EC545] md:text-3xl">
            {result.score}
          </p>
        </div>
        <div className="w-1/4 min-w-fit space-y-1 text-center">
          <p className="font-semibold text-nowrap">Jawaban yang benar</p>
          <p className="text-xl font-black text-[#0EC545] md:text-3xl">
            <span>{result.total_benar}</span>
            <span className="text-gray-400">
              /{result.total_benar + result.total_salah}
            </span>
          </p>
        </div>
        <div className="w-1/4 min-w-fit space-y-1 text-center">
          <p className="font-semibold text-nowrap">Status</p>
          <p className="text-xl font-black text-[#0EC545] uppercase md:text-3xl">
            {result.condition === 'failed' ? 'Gagal' : 'Lulus'}
          </p>
        </div>
      </div>
    </div>
  )
}

function Pagination() {
  const status = useTaskQuiz((s) => s.status)
  const setConfirmed = useTaskQuiz((s) => s.setConfirmed)

  return (
    <div className="mt-4 flex flex-col">
      <Separator className="w-full" />
      <div className="mt-4 flex grow">
        {status === 'idle' && (
          <Button variant="yes" className="ml-auto" onClick={setConfirmed}>
            Mulai Quiz
            <ArrowRight />
          </Button>
        )}
        {status === 'running' && <ButtonMainAction />}
      </div>
    </div>
  )
}

function ButtonMainAction() {
  const ref = useRef<HTMLButtonElement | null>(null)
  const selected = useTaskQuiz((s) => s.selected)
  const quizId = useTaskQuiz((s) => s.quizId)
  const onChangeQuestion = useTaskQuiz((s) => s.onChangeQuestion)
  const isLast = selected.index === useTaskQuiz((s) => s.questions.length - 1)

  const updateStatus = (
    status: IQuestionStatus,
    explanation?: Maybe<string>
  ) => {
    useTaskQuiz.setState((x) => ({
      selected: {
        ...x.selected,
        status,
        explanation,
      },
    }))
  }

  // const checkAnswer = useMutation({
  //   mutationFn: trackQuizCheckAnswer.api,
  //   onMutate() {
  //     updateStatus('validating')
  //   },
  //   onSuccess(data) {
  //     const { is_correct: isCorrect, explanation } = data.answerQuizChapter
  //     updateStatus(isCorrect ? 'correct' : 'wrong', explanation)
  //     invalidateQueries(trackDetails.key)
  //   },
  // })

  const fireConfetti = async () => {
    if (!ref.current) return
    try {
      const rect = ref.current?.getBoundingClientRect?.()
      const x = rect.left + rect.width / 2
      const y = rect.top + rect.height / 2
      await confetti({
        origin: {
          x: x / window.innerWidth,
          y: y / window.innerHeight,
        },
      })
    } catch (error) {
      console.error('Confetti button error:', error)
    }
  }

  const onClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    // const needToCheck = selected.status === 'idle'
    // if (!needToCheck) {
    onChangeQuestion(selected.index + 1)
    // } else {
    //   checkAnswer
    //     .mutateAsync({
    //       req: {
    //         chapter_id: quizId,
    //         answer: {
    //           answers: selected.answerId,
    //           question_id: selected.id,
    //         },
    //       },
    //     })
    //     .then(({ answerQuizChapter: { is_correct } }) => {
    //       is_correct && fireConfetti()
    //     })
    // }
  }

  // const isPending = checkAnswer.isPending
  if (!selected) return null

  return (
    <>
      <ButtonLoading
        ref={ref}
        className="ml-auto min-w-44"
        variant={'yes'}
        onClick={onClick}
        disabled={selected.status === 'validating' || !selected.answerId.length}
      >
        <div className="flex items-center justify-center">
          {isLast ? (
            'Submit Quiz'
          ) : (
            <>
              Lanjut <ArrowRight className="ml-2" />
            </>
          )}
        </div>
      </ButtonLoading>
    </>
  )
}

// function PaginationArrow() {
//   const selected = useTaskQuiz((s) => s.selected)
//   const questionsCount = useTaskQuiz((s) => s.questionsCount)
//   const onSelectQuestion = useTaskQuiz((s) => s.onSelectQuestion)
//   const quizId = useTaskQuiz((s) => s.quizId)

//   const isValidating = selected.status === 'validating'
//   const isCorrect = selected.status === 'correct'
//   const isFirst = selected.index === 0
//   const isLast = selected.index === questionsCount - 1

//   const { mutateAsync, isPending } = useMutation({
//     mutationFn: trackQuizCheckAnswer.api,
//     onSuccess(data, variables, context) {
//       alert(data.answerQuizChapter.is_correct ? 'Benar' : 'salah')
//     },
//   })

//   const onClick = () => {
//     if (isCorrect) onSelectQuestion(selected.index + 1)
//     else {
//       mutateAsync({
//         req: {
//           chapter_id: quizId,
//           answer: {
//             answers: [selected.answerId],
//             question_id: selected.id,
//           },
//         },
//       })
//     }
//   }

//   return (
//     <div className="flex grow justify-between gap-2">
//       {!isFirst && (
//         <Button
//           size={'icon'}
//           variant={'outline'}
//           type="button"
//           className="mr-auto"
//           onClick={() => onSelectQuestion(selected.index - 1)}
//           disabled={isValidating}
//         >
//           <ArrowLeft />
//         </Button>
//       )}
//       {!isLast && (
//         <ButtonLoading
//           size={'icon'}
//           variant={'outline'}
//           type="button"
//           className="ml-auto"
//           onClick={onClick}
//           // disabled={!isCorrect}
//           spinnerClassName={cn('fill-red-600')}
//           isLoading={isPending}
//         >
//           <ArrowRight />
//         </ButtonLoading>
//       )}
//     </div>
//   )
// }

function QuestionContent({ ...props }: IQuestionItem) {
  const taskType = props.type

  return (
    <div className="flex grow flex-col gap-4 pb-8">
      {taskType === 'multiple_choice' && (
        <TaskMultipleChoice
          key={props.id}
          {...props}
          onSelect={(id) =>
            useTaskQuiz.setState((old) => ({
              ...old,
              selected: { ...old.selected, answerId: [id] },
            }))
          }
        />
      )}
      {taskType === 'listening' && (
        <TaskListening
          key={props.id}
          {...props}
          onChange={(answerIds) => {
            useTaskQuiz.setState((old) => ({
              ...old,
              selected: { ...old.selected, answerId: answerIds },
            }))
          }}
        />
      )}
      <Explanation />
    </div>
  )
}

function Explanation() {
  const selected = useTaskQuiz((s) => s.selected)

  if (selected.status !== 'wrong' || !selected.explanation) return null
  return (
    <div className="mx-auto w-full max-w-[600px] grow rounded-lg border border-[#FFC28B] bg-yellow-500/10 p-3">
      <p className="font-semibold">Jawaban Salah</p>
      <div dangerouslySetInnerHTML={{ __html: selected.explanation }} />
    </div>
  )
}

function Timer() {
  const countdown = useTaskQuiz((s) => s.countdown)

  return (
    <div className="w-24 shrink-0 text-center">
      <p className="text-sm">Sisa Waktu</p>
      <p className="text-persian-red-600 text-lg font-bold">
        {formatTime(countdown)}
      </p>
    </div>
  )
}

function DialogTimeout() {
  const status = useTaskQuiz((s) => s.status)
  const questionsDone = useTaskQuiz(
    (s) => s.questions.filter((q) => q.status !== 'idle').length
  )
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (status === 'timeout') {
      setOpen(true)
    }
  }, [status])

  const onClick = () => {
    setOpen(false)
    useTaskQuiz.setState((s) => ({
      status: 'finished',
    }))
  }

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent noCloseIcon>
        <DialogHeader>
          <DialogTitle>Waktumu habis</DialogTitle>
          <DialogDescription>{questionsDone} soal terjawab</DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-center py-3">
          <Button variant={'yes'} size={'lg'} onClick={onClick}>
            Lihat Nilai
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
